import { supabase } from './supabase';

export type ReparatieStatus =
  | 'Aanvraag verstuurd'
  | 'Offerte ontvangen'
  | 'Offerte naar klant'
  | 'Klant akkoord'
  | 'Bij reparateur'
  | 'Terug in wasserij'
  | 'Afgerond'
  | 'Geannuleerd'
  | 'Geweigerd';

export interface ReparateurDto {
  id: string;
  naam: string;
  email: string | null;
  margePercentage: number | null;
}

export interface RitInfo {
  id: string;
  datum: string | null;
  routeNaam: string | null;
  status: string | null;
}

export type ReparatieIntentStatus = 'Open' | 'Bevestigd' | 'Niet_Nodig';

// ─── Fase-model (spiegel van api/_lib/reparatie-fase.ts) ────────────────────
// De API berekent fase, label en volgende stap; de UI rendert alleen.

export type KleedFase =
  | 'bij_klant'
  | 'in_wasserij'
  | 'heen_gepland'
  | 'bij_reparateur'
  | 'klaar'
  | 'terug_gepland'
  | 'terug_in_wasserij';

export const KLEED_FASEN: KleedFase[] = [
  'bij_klant',
  'in_wasserij',
  'heen_gepland',
  'bij_reparateur',
  'klaar',
  'terug_gepland',
  'terug_in_wasserij',
];

export const FASE_STAP_LABEL: Record<KleedFase, string> = {
  bij_klant: 'Bij klant',
  in_wasserij: 'In wasserij',
  heen_gepland: 'Heen-rit',
  bij_reparateur: 'Bij reparateur',
  klaar: 'Klaar',
  terug_gepland: 'Terug-rit',
  terug_in_wasserij: 'Terug in wasserij',
};

export type VolgendeStapActie =
  | 'inkoop'
  | 'send-quote'
  | 'akkoord'
  | 'plan-heen'
  | 'markeer-gebracht'
  | 'rond-in-huis-af'
  | 'plan-terug'
  | 'wacht-reparateur'
  | 'wacht-klant'
  | 'wacht-ophaal'
  | 'wacht-was'
  | 'wacht-chauffeur'
  | 'wacht-reparatie'
  | 'wacht-terug'
  | 'geen';

export interface VolgendeStap {
  actie: VolgendeStapActie;
  tekst: string;
  primaireKnop: string | null;
  fase: KleedFase | null;
}

export interface RepairItemSummary {
  wiId: string;
  wiNaam: string;
  /** Echte afbeelding uit de eigen media-laag; null = geen foto om te tonen. */
  fotoUrl: string | null;
  /** Foto-PAGINA (eigen timeline of CompanyCam) — een link, geen <img src>. */
  fotosUrl?: string | null;
  opdrachtTekst: string | null;
  status: string | null;
  reparatieKlaarOp: string | null;
  reparatieAfgeleverdOp: string | null;
  reparatieOpgehaaldOp: string | null;
  geplandeRitHeen: RitInfo | null;
  geplandeRitTerug: RitInfo | null;
  /** Fysieke fase + zin, berekend door de API (reparatie-fase.ts). */
  fase?: KleedFase;
  faseLabel?: string;
  // Eindfoto's bij klaarmelden (Fase 2) — Supabase storage-paths, gesigned bij render
  eindfotoPaths?: string[];
  klantverzoekReparatie?: string | null;
  klantverzoekStatus?: ReparatieIntentStatus | null;
  klantverzoekBron?: string | null;
  klantverzoekNotitie?: string | null;
}

export interface ReparatieIntentDto {
  wiId: string;
  klantverzoek: string | null;
  status: ReparatieIntentStatus | null;
  bron: string | null;
  notitie: string | null;
}

export interface IntentResponse extends ApiResponse {
  intent?: ReparatieIntentDto;
}

export interface ReparatieOpdracht {
  id: string;
  opdrachtNummer: string;
  status: ReparatieStatus;
  /**
   * 'intern' = eigen schade, coulance of garantie. Dan is er geen klantpad:
   * send-quote weigert server-side en de klant ziet en betaalt niets.
   * Ontbreekt op oude opdrachten van vóór migratie 118 — lees als 'klant'.
   */
  betaler?: Betaler;
  reparateurId: string | null;
  reparateurNaam: string | null;
  inkoopBedrag: number | null;
  margePercentage: number | null;
  verkoopBedrag: number | null;
  token: string | null;
  offerteAangevraagdOp: string | null;
  offerteOntvangenOp: string | null;
  klantOfferteVerstuurdOp: string | null;
  klantAkkoordOp: string | null;
  gebrachtOp: string | null;
  opgehaaldOp: string | null;
  aantalKleden: number;
  items: RepairItemSummary[];
  /** Eén zin + één primaire actie, berekend door de API. */
  volgendeStap?: VolgendeStap;
  // Offerte-intake door reparateur (Fase 1)
  doorlooptijdWerkdagen?: number | null;
  voorbeeldFotoPaths?: string[];
  voorbeeldToelichting?: string | null;
  // Klant-weiger (Fase 0)
  weigerReden?: string | null;
  // Reparateur "kan niet" (Fase 2)
  reparateurKanNiet?: boolean;
  afwijzingReden?: string | null;
  // Office-annulering
  geannuleerdOp?: string | null;
  annuleringReden?: string | null;
  // Alleen gevuld op /reparaties overzicht (cross-WO context)
  werkOrderId?: string | null;
  werkOrderNummer?: string | null;
  klantNaam?: string | null;
}

export interface ReparatieKleed {
  id: string;
  itemNumber: string;
  name: string;
}

export interface RouteOption {
  routeId: string;
  label?: string | null;
  datum?: string | null;
  datumISO?: string | null;
  tijdVanaf?: string | null;
  tijdTot?: string | null;
  capaciteitBeschikbaar?: number | null;
  capaciteitTotaal?: number | null;
  isFull?: boolean | null;
  isVandaag?: boolean | null;
  isNietVoorstellen?: boolean | null;
  waarschuwing?: string | null;
  routeNaam?: string | null;
}

export interface MarkKleedResponse extends ApiResponse {
  alreadyAccepted?: boolean;
  geplandeRitId?: string | null;
  geplandeRitDatum?: string | null;
  routeWarnings?: string[];
}

export interface PlanRitResponse extends ApiResponse {
  geplandeRitId?: string | null;
  /**
   * Apex geeft de datum van de zojuist gekoppelde rit terug. Nodig omdat de
   * office-app de heen/terug-rit uit de mirror leest en die pas bij de
   * volgende sync bij is: zonder deze waarde toont de kaart direct na het
   * plannen nog "Wacht op route".
   */
  geplandeRitDatum?: string | null;
}

export type Betaler = 'klant' | 'intern';

/** Eén plek voor de vraag "mag deze opdracht het klantpad gebruiken?". */
/** BTW op reparaties; het factuurregel-product staat op 21%. */
export const REPARATIE_BTW_PCT = 21;

export interface ReparatiePrijsUitkomst {
  /** Wat de reparateur ons rekent, inclusief BTW. Dit slaat de server op. */
  inkoopIncl: number;
  /** Wat de klant betaalt, inclusief BTW. */
  verkoopIncl: number;
  /** Zelfde bedrag zonder BTW -- kantoor onderhandelt in excl. */
  verkoopExcl: number;
}

/**
 * De prijsregel van een reparatie, vanuit de prijs zoals de reparateur hem
 * offreert (exclusief BTW).
 *
 * Eén definitie voor beide schermen, want ze liepen uit elkaar: het
 * inkoopscherm rekende de marge over het EXCLUSIEVE bedrag en zette
 * "incl. BTW" onder de uitkomst. Bij EUR 1.000 en 50% toonde het EUR 1.500
 * terwijl de server EUR 1.815 opsloeg -- kantoor zou een offerte versturen
 * met een bedrag dat het nooit gezien had (WB-322781, 04-09-2026).
 *
 * De marge werkt op de INCLUSIEVE kostprijs, precies zoals
 * `berekenVerkoopBedrag` op de server en het SF-formuleveld
 * `Verkoop_Bedrag__c = Inkoop_Bedrag__c * (1 + Marge_Percentage__c)`.
 */
export function reparatiePrijs(inkoopExclBtw: number, margePercentage: number): ReparatiePrijsUitkomst | null {
  if (!Number.isFinite(inkoopExclBtw) || inkoopExclBtw <= 0) return null;
  if (!Number.isFinite(margePercentage) || margePercentage < 0) return null;
  const btw = 1 + REPARATIE_BTW_PCT / 100;
  const inkoopIncl = rond(inkoopExclBtw * btw);
  const verkoopIncl = rond(inkoopIncl * (1 + margePercentage / 100));
  return { inkoopIncl, verkoopIncl, verkoopExcl: rond(verkoopIncl / btw) };
}

/** Van een opgeslagen inkoopbedrag (incl.) terug naar wat de reparateur offreerde. */
export function inkoopExclUitIncl(inkoopIncl: number): number {
  return rond(inkoopIncl / (1 + REPARATIE_BTW_PCT / 100));
}

function rond(n: number): number {
  return Math.round(n * 100) / 100;
}

export function isInterneReparatie(o: { betaler?: Betaler | null }): boolean {
  return o.betaler === 'intern';
}

export interface CreateReparatiePayload {
  workOrderId: string;
  reparateurAccountId: string;
  opdrachtAlgemeen: string;
  items: Array<{ wiId: string; opdrachtTekst: string }>;
  betaler?: Betaler;
  /** Reparateur heeft al geprijsd (bv. op klantfoto's vóór de ophaal): geen
   *  aanvraagmail, opdracht start op 'Offerte ontvangen'. */
  offerteVooraf?: { inkoopBedrag: number; margePercentage?: number | null } | null;
}

export interface ApiResponse {
  success: boolean;
  message?: string;
  errorCode?: string;
}

export interface CreateReparatieResponse extends ApiResponse {
  opdrachtId?: string;
  opdrachtNummer?: string;
  status?: ReparatieStatus;
  token?: string;
  verkoopBedrag?: number;
  mailSent?: boolean;
  mailSkipped?: boolean;
  mailError?: string;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`;
  return h;
}

async function get<T>(path: string): Promise<T> {
  const headers = await authHeaders();
  const r = await fetch(path, { headers });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || err.detail || `Request failed (${r.status})`);
  }
  return r.json() as Promise<T>;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const headers = await authHeaders();
  const r = await fetch(path, { method: 'POST', headers, body: JSON.stringify(body) });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: 'Request failed' }));
    // Guard-weigeringen (409) en domein-fouten dragen `message`, geen `error`.
    throw new Error(err.error || err.detail || err.message || `Request failed (${r.status})`);
  }
  return r.json() as Promise<T>;
}

async function patch<T>(path: string, body: unknown): Promise<T> {
  const headers = await authHeaders();
  const r = await fetch(path, { method: 'PATCH', headers, body: JSON.stringify(body) });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || err.detail || `Request failed (${r.status})`);
  }
  return r.json() as Promise<T>;
}

export async function listReparaties(workOrderId: string): Promise<ReparatieOpdracht[]> {
  const data = await get<{ success: boolean; opdrachten: ReparatieOpdracht[] }>(
    `/api/orders/reparatie/list?workOrderId=${encodeURIComponent(workOrderId)}`
  );
  return Array.isArray(data.opdrachten) ? data.opdrachten : [];
}

export async function listReparateurs(): Promise<ReparateurDto[]> {
  const data = await get<{ success: boolean; reparateurs: ReparateurDto[] }>(
    `/api/orders/reparatie/reparateurs`
  );
  return Array.isArray(data.reparateurs) ? data.reparateurs : [];
}

// ==================== Reparateur-registry (admin /reparateurs) ====================

export interface ReparateurMutationResponse extends ApiResponse {
  reparateurId?: string;
  naam?: string;
  email?: string | null;
  margePercentage?: number | null;
  actief?: boolean;
}

/** Admin-gated lijst (zelfde data als listReparateurs, via /api/reparateurs). */
export async function listReparateursAdmin(): Promise<ReparateurDto[]> {
  const data = await get<{ success: boolean; reparateurs: ReparateurDto[] }>(`/api/reparateurs`);
  return Array.isArray(data.reparateurs) ? data.reparateurs : [];
}

export function createReparateur(input: {
  naam: string;
  email: string;
  margePercentage?: number | null;
}): Promise<ReparateurMutationResponse> {
  return post<ReparateurMutationResponse>('/api/reparateurs', input);
}

export function updateReparateur(input: {
  reparateurId: string;
  naam?: string;
  email?: string;
  margePercentage?: number | null;
  actief?: boolean;
}): Promise<ReparateurMutationResponse> {
  return patch<ReparateurMutationResponse>('/api/reparateurs', input);
}

export function createReparatie(payload: CreateReparatiePayload): Promise<CreateReparatieResponse> {
  return post<CreateReparatieResponse>('/api/orders/reparatie/create', payload);
}

export function updateReparatiePurchase(args: {
  opdrachtId: string;
  inkoopBedrag: number;
  margePercentage?: number | null;
}): Promise<ApiResponse & { verkoopBedrag?: number }> {
  return post('/api/orders/reparatie/update-purchase', args);
}

export type AkkoordBron = 'e-mail' | 'telefoon' | 'whatsapp' | 'balie';

/**
 * Legt een klant-akkoord vast dat buiten de token-link om binnenkwam. Bewijs
 * is verplicht (server weigert zonder). Zelfde uitkomst als de klant-klik.
 */
export function legReparatieAkkoordVast(args: {
  opdrachtId: string;
  bron: AkkoordBron;
  bewijs: string;
}): Promise<ApiResponse & { alreadyAccepted?: boolean; verkoopBedrag?: number }> {
  return post('/api/orders/reparatie/akkoord-vastleggen', args);
}

/**
 * Reparatie bij de interne reparateur (Havelte) in één handeling afronden:
 * geen rit, geen statuswissel, het kleed blijft staan. De bevestiging (wie in
 * de wasserij zei dat het werk gedaan is) is verplicht; de server weigert
 * zonder.
 */
export function rondReparatieInHuisAf(args: {
  opdrachtId: string;
  bevestiging: string;
}): Promise<ApiResponse & { alreadyAccepted?: boolean; routeWarnings?: string[] }> {
  return post('/api/orders/reparatie/rond-in-huis-af', args);
}

/**
 * Prijs aanpassen ná klant-akkoord. De klantprijs (incl. BTW) is het
 * uitgangspunt — met een klant spreek je een bedrag af, de marge is de
 * uitkomst. Vóór het akkoord loopt dit via updateReparatiePurchase.
 */
export function wijzigReparatiePrijs(args: {
  opdrachtId: string;
  inkoopBedrag: number;
  klantprijsIncl: number;
  reden: string;
}): Promise<
  ApiResponse & {
    verkoopBedrag?: number;
    margePercentage?: number;
    klantprijsIncl?: number;
    betaallink?: string | null;
  }
> {
  return post('/api/orders/reparatie/wijzig-prijs', args);
}

export interface OffertePreview extends ApiResponse {
  /** 'eigen' = zo gaat de mail eruit; 'apex' = Salesforce stuurt de VF-template. */
  modus?: 'eigen' | 'apex';
  to?: string | null;
  subject?: string;
  html?: string;
  verkoopBedrag?: number | null;
}

/** Rendert de klant-offertemail zonder iets te versturen of te wijzigen. */
export function previewReparatieOfferte(args: {
  opdrachtId: string;
  eigenBericht?: string | null;
}): Promise<OffertePreview> {
  return post('/api/orders/reparatie/offerte-preview', args);
}

export function sendReparatieQuote(opdrachtId: string, eigenBericht?: string | null): Promise<ApiResponse> {
  return post('/api/orders/reparatie/send-quote', { opdrachtId, eigenBericht: eigenBericht ?? null });
}

/** Guard bewust passeren (bv. "Albert heeft het zelf gebracht"); reden verplicht. */
export interface StempelOpties {
  force?: boolean;
  reden?: string;
}

export function markReparatieDelivered(opdrachtId: string, opts: StempelOpties = {}): Promise<ApiResponse> {
  return post('/api/orders/reparatie/mark-delivered', { opdrachtId, ...opts });
}

export function markReparatiePickedUp(opdrachtId: string, opts: StempelOpties = {}): Promise<ApiResponse> {
  return post('/api/orders/reparatie/mark-picked-up', { opdrachtId, ...opts });
}

export function sendReparatieMailAgain(opdrachtId: string): Promise<ApiResponse & { mailSent?: boolean }> {
  return post('/api/orders/reparatie/send-mail-again', { opdrachtId });
}

/** Annuleert een opdracht — alleen vóór klant-akkoord (server bewaakt dit). */
export function cancelReparatie(args: {
  opdrachtId: string;
  reden?: string;
}): Promise<ApiResponse> {
  return post('/api/orders/reparatie/cancel', args);
}

// ==================== Fase 1.5 — logistiek ====================

export function markReparatieKleedKlaar(wiId: string): Promise<MarkKleedResponse> {
  return post('/api/orders/reparatie/mark-klaar', { wiId });
}

export function unmarkReparatieKleedKlaar(wiId: string): Promise<MarkKleedResponse> {
  return post('/api/orders/reparatie/unmark-klaar', { wiId });
}

export function markReparatieKleedAfgeleverd(wiId: string, opts: StempelOpties = {}): Promise<MarkKleedResponse> {
  return post('/api/orders/reparatie/mark-afgeleverd', { wiId, ...opts });
}

export function markReparatieKleedOpgehaald(wiId: string, opts: StempelOpties = {}): Promise<MarkKleedResponse> {
  return post('/api/orders/reparatie/mark-opgehaald', { wiId, ...opts });
}

/**
 * "Gebracht" terugdraaien. `vorigeWiStatus` alleen meegeven als de server hem
 * niet uit het mark-event kan halen (errorCode VORIGE_STATUS_ONBEKEND);
 * null = nog bij klant.
 */
export function unmarkReparatieKleedAfgeleverd(args: {
  wiId: string;
  reden: string;
  vorigeWiStatus?: string | null;
}): Promise<MarkKleedResponse> {
  return post('/api/orders/reparatie/unmark-afgeleverd', args);
}

export function unmarkReparatieKleedOpgehaald(args: { wiId: string; reden: string }): Promise<MarkKleedResponse> {
  return post('/api/orders/reparatie/unmark-opgehaald', args);
}

export function planReparatieHeenRit(args: {
  wiId: string;
  geplandeRitId: string;
}): Promise<PlanRitResponse> {
  return post('/api/orders/reparatie/plan-heen', args);
}

export function planReparatieTerugRit(args: {
  geplandeRitId: string;
  wiIds: string[];
}): Promise<PlanRitResponse> {
  return post('/api/orders/reparatie/plan-terug', args);
}

export async function listAllReparaties(filters?: {
  status?: ReparatieStatus;
  reparateurId?: string;
  includeAfgerond?: boolean;
}): Promise<ReparatieOpdracht[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.reparateurId) params.set('reparateurId', filters.reparateurId);
  if (filters?.includeAfgerond) params.set('includeAfgerond', 'true');
  const qs = params.toString();
  const data = await get<{ success: boolean; opdrachten: ReparatieOpdracht[] }>(
    `/api/orders/reparatie/overzicht${qs ? '?' + qs : ''}`
  );
  return Array.isArray(data.opdrachten) ? data.opdrachten : [];
}

// ==================== Pre-pickup reparatie-intent ====================

export function setReparatieIntent(args: {
  wiId: string;
  klantverzoek: string;
}): Promise<IntentResponse> {
  return post('/api/orders/reparatie/intent-set', args);
}

export function clearReparatieIntent(wiId: string): Promise<IntentResponse> {
  return post('/api/orders/reparatie/intent-clear', { wiId });
}

export function confirmReparatieIntent(args: {
  wiId: string;
  notitie?: string;
}): Promise<IntentResponse> {
  return post('/api/orders/reparatie/intent-confirm', args);
}

export function denyReparatieIntent(args: {
  wiId: string;
  notitie?: string;
}): Promise<IntentResponse> {
  return post('/api/orders/reparatie/intent-deny', args);
}

// ==================== Office "Te plannen"-signaal ====================

export interface IntentTodoItem {
  wiId: string;
  wiName: string;
  klantverzoek: string | null;
  bron: string | null;
  werkOrderId: string | null;
  werkOrderName: string | null;
  klantNaam: string | null;
  datumBevestigd: string | null;
}

export async function fetchReparatieIntentTodoCount(): Promise<number> {
  const data = await get<{ count: number }>('/api/orders/reparatie/intent-todo?countOnly=1');
  return typeof data.count === 'number' ? data.count : 0;
}

export async function fetchReparatieIntentTodoList(): Promise<IntentTodoItem[]> {
  const data = await get<{ items: IntentTodoItem[] }>('/api/orders/reparatie/intent-todo');
  return Array.isArray(data.items) ? data.items : [];
}

// ==================== Office "Offerte ontvangen"-signaal ====================

export interface OfferteTodoItem {
  opdrachtId: string;
  opdrachtNummer: string;
  reparateurId: string | null;
  reparateurNaam: string | null;
  inkoopBedrag: number | null;
  verkoopBedrag: number | null;
  margePercentage: number | null;
  doorlooptijdWerkdagen: number | null;
  voorbeeldToelichting: string | null;
  voorbeeldFotoPaths: string[];
  werkOrderId: string | null;
  werkOrderName: string | null;
  klantNaam: string | null;
  offerteOntvangenOp: string | null;
}

export async function fetchReparatieOfferteTodoCount(): Promise<number> {
  const data = await get<{ count: number }>('/api/orders/reparatie/offerte-todo?countOnly=1');
  return typeof data.count === 'number' ? data.count : 0;
}

export async function fetchReparatieOfferteTodoList(): Promise<OfferteTodoItem[]> {
  const data = await get<{ items: OfferteTodoItem[] }>('/api/orders/reparatie/offerte-todo');
  return Array.isArray(data.items) ? data.items : [];
}

/** Verse signed URLs voor voorbeeld-foto's (bucket reparatie-fotos). */
export async function fetchReparatieVoorbeeldUrls(paths: string[]): Promise<Array<string | null>> {
  if (!paths || paths.length === 0) return [];
  const data = await post<{ urls: Array<string | null> }>('/api/orders/reparatie/foto-url', { paths });
  return Array.isArray(data.urls) ? data.urls : [];
}

export async function listReparatieRoutesForReparateur(
  reparateurId: string,
  daysAhead = 21
): Promise<RouteOption[]> {
  const data = await get<{ success: boolean; options: RouteOption[] }>(
    `/api/orders/reparatie/routes-for-reparateur?reparateurId=${encodeURIComponent(
      reparateurId
    )}&daysAhead=${daysAhead}`
  );
  return Array.isArray(data.options) ? data.options : [];
}
